<template>
  <div>
    <PriceWrapper
      v-if="!deal"
      :is-from-google-shopping
      :mobile-plan
      :price
      :price-due-today
      :product
      :selected-offer
      :with-assurance-fee
    >
      <template #refurbished-price>
        {{
          /* empty template to avoid rendering the refurbished price
        (default content of the slot if nothing is provided) */ ''
        }}
      </template>
      <template #first-row-price v-if="buybackOffer && !hasOriginalPrice">
        {{
          /* empty template to avoid rendering the first row
        (default content of the slot if nothing is provided) */ ''
        }}
      </template>
      <template #first-row-price v-else-if="!buybackOffer || hasOriginalPrice">
        <span
          v-if="!buybackOffer"
          class="heading-2"
          data-qa="productpage-product-price"
          data-test="productpage-product-price"
        >
          {{ i18n.price(price) }}
        </span>
        <PriceMainLabel
          v-if="!buybackOffer"
          :buyback-offer="null"
          :is-from-google-shopping
          :mobile-plan-offer="mobilePlan"
        />
        <OriginalPrice
          v-if="
            buybackOffer && hasOriginalPrice && product.type !== 'Brand new'
          "
          :is-discount-reassurance-displayed="Boolean(selectedOffer.discount)"
          :price-when-new="product.priceWhenNew"
        />
        <DealTag
          v-if="
            buybackOffer && flashSalesDiscount && isFlashSalesExperimentEnabled
          "
          :icon="IconBoltFilled"
          :label="
            i18n(translations.flashSalesTag, {
              price: i18n.price(flashSalesDiscount),
            })
          "
        />
        <YouSave
          v-else-if="
            buybackOffer &&
            hasOriginalPrice &&
            hasSavings &&
            selectedOffer.price
          "
          :is-discount-reassurance-displayed="Boolean(selectedOffer.discount)"
          :mobile-plan
          :price="selectedOffer.price"
          :price-when-new="product.priceWhenNew"
        />
      </template>
      <template #second-row-price v-if="buybackOffer">
        <PriceDueTodayLabel :buyback-offer :price-due-today />
      </template>
      <template #third-row-price v-if="buybackOffer">
        <span
          class="heading-2"
          data-qa="productpage-product-price"
          data-test="productpage-product-price"
        >
          {{ i18n.price(price) }}
        </span>
        <PriceMainLabel :buyback-offer />
        <RevTag
          v-if="product.type === 'Brand new'"
          :label="i18n(translations.newTaglabel)"
          variant="info"
        />
      </template>
    </PriceWrapper>

    <FlashSalesPrice
      v-else-if="deal?.type === 'flash_sales'"
      :buyback-offer
      :is-from-google-shopping
      :mobile-plan
      :price
      :product
      :selected-offer
      :variant
      :with-assurance-fee
      :without-price-due-today="!priceDueToday"
    />

    <SellerFlashSalesPrice
      v-else-if="deal?.type === 'seller_flash_sales'"
      :buyback-offer
      :is-from-google-shopping
      :mobile-plan
      :price
      :product
      :selected-offer
      :variant
      :with-assurance-fee
      :without-price-due-today="!priceDueToday"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models/Price'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-partnerships/mobile-plan/types/mobile-plan-offers'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useBuybackOffer'
import DealTag from '@backmarket/nuxt-layer-recommendation/DealTag.vue'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevTag } from '@ds/components/Tag'
import { IconBoltFilled } from '@ds/icons/IconBoltFilled'
import type { ReadonlyObjectDeep } from 'type-fest/source/readonly-deep'

import FlashSalesPrice from './FlashSalesPrice.vue'
import translations from './Price.translations'
import PriceWrapper from './PriceWrapper.vue'
import SellerFlashSalesPrice from './SellerFlashSalesPrice.vue'
import OriginalPrice from './components/OriginalPrice/OriginalPrice.vue'
import PriceDueTodayLabel from './components/PriceLabel/components/PriceDueTodayLabel.vue'
import PriceMainLabel from './components/PriceLabel/components/PriceMainLabel.vue'
import YouSave from './components/YouSave/YouSave.vue'

const props = withDefaults(
  defineProps<{
    selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
    price: Price
    priceDueToday?: Price | null
    product: GetProductResponse
    mobilePlan?: ReadonlyObjectDeep<MobilePlanOffer>
    flashSalesDiscount?: Price
    hasSavings?: boolean
    hasOriginalPrice?: boolean
    withAssuranceFee?: boolean
    isFromGoogleShopping?: boolean
    variant?: 'default' | 'compact'
  }>(),
  {
    mobilePlan: undefined,
    priceDueToday: undefined,
    flashSalesDiscount: undefined,
    hasSavings: true,
    hasOriginalPrice: true,
    withAssuranceFee: false,
    isFromGoogleShopping: false,
    variant: 'default',
  },
)

const experiments = useExperiments()
const i18n = useI18n()
const { buybackOffer } = useBuybackOffer()

const isFlashSalesExperimentEnabled =
  experiments['experiment.flashSales'] === 'enabled'

const isFlashSalesOnPDPEnabled = computed(
  () => experiments['experiment.flashSalesOnPDP'] === 'enabled',
)

const deal = computed(() =>
  isFlashSalesOnPDPEnabled.value ? props.selectedOffer?.deals?.[0] : null,
)
</script>
